"use client";

import { createContext, useContext } from "react";
import type { Tenant, TenantProject } from "@/lib/tenant";
import type { ProjectConfigs } from "@/types";

/**
 * The tenant's project, handed down from the server.
 *
 * This deliberately does *not* go through react-query, unlike every other read
 * from the backend. The legacy app fetched the project on boot because it was
 * fully client-rendered; here the root layout already resolves it server-side to
 * render the title, theme and `<html lang>`, and it never changes while the app
 * is open. Passing that same object down costs nothing, whereas a client query
 * would mean a second round trip, a loading state, and a window where the UI
 * disagrees with the server-rendered shell.
 *
 * `Tenant`/`TenantProject` are imported as types only — `@/lib/tenant` pulls in
 * `next/headers`, which must not reach a client bundle.
 */
export interface ProjectContextValue {
  /** Null when the host matches no project — see the unknown-tenant page. */
  project: TenantProject | null;
  tenant: Tenant;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);

export function ProjectProvider({
  project,
  tenant,
  children,
}: ProjectContextValue & { children: React.ReactNode }) {
  // The value is stable for the lifetime of the page, so there is nothing to
  // memoize: a new object per render would only matter if it changed identity
  // between renders of the same tree, and these props come from the server.
  return (
    <ProjectContext.Provider value={{ project, tenant }}>
      {children}
    </ProjectContext.Provider>
  );
}

function useProjectContext(): ProjectContextValue {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error("useProject must be used inside <ProjectProvider>");
  }
  return context;
}

/** The tenant's project, or null when the host matches none. */
export function useProject(): TenantProject | null {
  return useProjectContext().project;
}

/** The current request's tenant (host, slug, origin). */
export function useTenant(): Tenant {
  return useProjectContext().tenant;
}

/**
 * The project's Squidex-authored settings.
 *
 * Never null — an unresolved project yields an empty config, so callers read
 * `configs.mapZoom ?? default` instead of guarding the whole object first. Every
 * field is optional anyway (see `@/types/project`).
 */
export function useProjectConfigs(): ProjectConfigs {
  return useProjectContext().project?.config ?? {};
}
